import {
  CANADA_LOCATIONS,
  EU_LOCATIONS,
  UK_LOCATIONS,
  US_LOCATIONS,
} from "@otta/search/constants/locations";
import {
  Currency,
  Location,
  UserPreferencesReviewQuery,
} from "@otta/search/schema";
import { LOCATION_NAME } from "@otta/search/utils/locations";
import { currencyPrefix } from "@otta/search/utils/currencies";

function formatSalaryAmount(amount: string): string {
  const value = parseFloat(amount);
  // it shouldn't happen since the minimum is 10k, but just in case
  if (value < 10000) {
    return "10K";
  }
  return `${Math.floor(value / 1000)}k`;
}

export function countByLocation(locations: { location: Location }[]): number[] {
  return [
    locations.filter(l => US_LOCATIONS.has(l.location)).length,
    locations.filter(l => UK_LOCATIONS.has(l.location)).length,
    locations.filter(l => EU_LOCATIONS.has(l.location)).length,
    locations.filter(l => CANADA_LOCATIONS.has(l.location)).length,
  ];
}

export function determineCurrency(locations: Location[]): Currency {
  switch (true) {
    case !locations.length:
      return Currency.Usd;
    case locations.some(l => US_LOCATIONS.has(l)):
      return Currency.Usd;
    case locations.some(l => CANADA_LOCATIONS.has(l)):
      return Currency.Cad;
    case locations.some(l => UK_LOCATIONS.has(l)):
      return Currency.Gbp;
    case locations.some(l => EU_LOCATIONS.has(l)):
      return Currency.Eur;
    default:
      return Currency.Usd;
  }
}

const jobMapping: Record<string, string> = {
  internship: "Internships",
  entryLevel: "Entry-level/graduate",
  oneToTwoYears: "Junior (1–2 years)",
  threeToFourYears: "Mid-level (3–4 years)",
  fiveToEightYears: "Senior (5–8 years)",
  ninePlusYears: "Expert & Leadership (9+ years)",
};

function mapOrNotSelected<T>(
  arr: T[],
  mapper: (item: T) => string | null,
  predicate?: (item: T) => boolean
): string[] {
  if (arr.length === 0) return ["Not selected"];

  const filtered = predicate ? arr.filter(predicate) : arr;

  const result = filtered.map(mapper).filter((v): v is string => v !== null);

  return result.length > 0 ? result : ["Not selected"];
}

function joinOrNotSelected<T>(
  arr: T[],
  mapper: (item: T) => string | null,
  predicate?: (item: T) => boolean
): string[] {
  return mapOrNotSelected(arr, mapper, predicate);
}

function formatCompanyQualitiesWithValidation<T>(
  arr: T[],
  mapper: (item: T) => string | null
): string[] {
  if (arr.length === 0) return ["Not selected"];

  const result = arr.map(mapper).filter((v): v is string => v !== null);

  if (result.length === 0) {
    return ["Not selected"];
  } else if (result.length < 3) {
    return [...result, `Select ${3 - result.length} more`];
  } else {
    return result;
  }
}

/**
 * Maps a user object to the API JSON format.
 */
export function formatUserToJobPreferencesReview(
  user: UserPreferencesReviewQuery["currentUser"] | null | undefined
) {
  if (!user) return null;

  // Check if user has any remote preferences (indicates "Remote" checkbox was checked)
  const hasRemotePreferences = (user.locationPreferences || []).some(l =>
    l.location.startsWith("REMOTE_")
  );

  return {
    role: {
      label: "Role",
      icon: "user",
      fields: [
        {
          name: "type",
          label: "Type of role",
          path: "function",
          value: joinOrNotSelected(
            user.jobSubFunctionPreferences,
            p => p.subFunction.value
          ),
        },
        {
          name: "level",
          label: "Level of role",
          path: "job-experience",
          value: mapOrNotSelected(
            Object.entries(user.jobExperiencePreference || {}),
            ([key]) => jobMapping[key],
            ([key, value]) => key !== "__typename" && value === true
          ),
        },
        {
          name: "salary",
          label: "Minimum salary",
          path: "salary",
          value: user.minSalaryRequirement
            ? [
                `${currencyPrefix(
                  user.minSalaryRequirement.currency
                )}${formatSalaryAmount(user.minSalaryRequirement.amount)}`,
              ]
            : ["Not selected"],
        },
        {
          name: "priorities",
          label: "Role priorities",
          path: "quality",
          value: formatCompanyQualitiesWithValidation(
            user.companyQualitiesPreferences,
            p => p.value
          ),
        },
      ],
    },
    location: {
      label: "Location",
      icon: "location",
      fields: [
        {
          name: "locations",
          label: "Locations",
          path: "location",
          value: (() => {
            const physicalLocations = mapOrNotSelected(
              user.locationPreferences,
              l => {
                const name = LOCATION_NAME[l.location];
                return name ? name : null;
              },
              l => !l.location.startsWith("REMOTE_")
            );

            const hasPhysicalLocations =
              !physicalLocations.includes("Not selected");

            if (hasRemotePreferences && hasPhysicalLocations) {
              return [...physicalLocations, "Remote"];
            } else if (hasRemotePreferences && !hasPhysicalLocations) {
              return ["Remote"];
            } else {
              return physicalLocations;
            }
          })(),
        },
        ...(hasRemotePreferences
          ? [
              {
                name: "remoteLocations",
                label: "Remote locations",
                path: "remote-location",
                value: mapOrNotSelected(
                  user.locationPreferences,
                  l => {
                    const name = LOCATION_NAME[l.location];
                    return name ? name : null;
                  },
                  l => l.location.startsWith("REMOTE_")
                ),
              },
            ]
          : []),
        {
          name: "visa",
          label: "Visa requirements",
          path: "visa-sponsorship",
          value: joinOrNotSelected(
            user.visaRequirementCountries,
            c => c.location
          ),
        },
        {
          name: "languages",
          label: "Additional languages",
          path: "language-requirement",
          value: mapOrNotSelected(
            user.languageRequirementPreferences,
            l => l.languageRequirement.value
          ),
        },
      ],
    },
    company: {
      label: "Company",
      icon: "company",
      fields: [
        {
          name: "size",
          label: "Company size",
          path: "company-size",
          value: joinOrNotSelected(
            user.companySizePreferences,
            s => s.companySize.value
          ),
        },
      ],
    },
    industries: {
      label: "Industries",
      icon: "industry",
      fields: [
        {
          name: "favourite",
          label: "Favourite industries",
          path: "positive-company-sectors",
          value: mapOrNotSelected(
            user.sectorTagPreferences,
            p => p.sectorTag.value,
            p => p.preference === true
          ),
        },
        {
          name: "disliked",
          label: "Disliked industries",
          path: "negative-company-sectors",
          value: mapOrNotSelected(
            user.sectorTagPreferences,
            p => p.sectorTag.value,
            p => p.preference === false
          ),
        },
      ],
    },
    technologies: {
      label: "Technologies",
      icon: "technology",
      fields: [
        {
          name: "favourite",
          label: "Favourite technologies",
          path: "positive-technologies",
          value: mapOrNotSelected(
            user.technologiesUsedPreferences,
            p => p.technologyUsed.value,
            p => ["POSITIVE", "VERY_POSITIVE"].includes(p.technologyPreference)
          ),
        },
        {
          name: "disliked",
          label: "Disliked technologies",
          path: "negative-technologies",
          value: mapOrNotSelected(
            user.technologiesUsedPreferences,
            p => p.technologyUsed.value,
            p => p.technologyPreference === "NEGATIVE"
          ),
        },
      ],
    },
  };
}
